import { useState } from 'react';

const STORAGE_KEY = 'juniper.table.itemsPerPage';
const DEFAULT_ITEMS_PER_PAGE = 20;

export const ITEMS_PER_PAGE_OPTIONS = [10, 15, 20, 30, 40, 50] as const;
export type ItemsPerPageOption = (typeof ITEMS_PER_PAGE_OPTIONS)[number];

export interface UseTablePaginationReturn {
  itemsPerPage: number;
  onItemsPerPageChange: (next: number) => void;
}

const isValidOption = (value: unknown): value is ItemsPerPageOption => {
  return (
    typeof value === 'number' &&
    (ITEMS_PER_PAGE_OPTIONS as readonly number[]).includes(value)
  );
};

const readStoredValue = (fallback: number): number => {
  const raw: string = localStorage.getItem(STORAGE_KEY);

  if (raw === null) {
    return fallback;
  }

  const parsed = JSON.parse(raw);

  if (isValidOption(parsed)) {
    return parsed;
  }

  return fallback;
};

export const useTablePagination = (
  defaultItemsPerPage: number = DEFAULT_ITEMS_PER_PAGE
): UseTablePaginationReturn => {
  const [itemsPerPage, setItemsPerPage] = useState<number>(() =>
    readStoredValue(defaultItemsPerPage)
  );

  const onItemsPerPageChange = (next: number) => {
    if (!isValidOption(next)) {
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setItemsPerPage(next);
  };

  return {
    itemsPerPage,
    onItemsPerPageChange,
  };
};
